import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { queryKeys } from '../lib/query-keys';
import type { NotificationDTO } from './notification-types';

interface NotificationsResponse {
  data: NotificationDTO[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  unread_count: number;
}

export function useNotifications(enabled = true) {
  return useQuery({
    queryKey: queryKeys.notifications.list(),
    queryFn: async () => {
      const response = await api.get<NotificationsResponse>('/notifications', {
        params: { limit: 20 },
      });
      return response.data;
    },
    enabled,
    refetchInterval: 30_000, // Poll every 30 seconds (DEC-018)
    refetchIntervalInBackground: false,
  });
}

export function useUnreadCount(enabled = true) {
  const { data } = useNotifications(enabled);
  return data?.unread_count ?? 0;
}

export function useMarkAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.put<{ data: { id: string; is_read: boolean } }>(
        `/notifications/${id}/read`,
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });
}

export function useMarkAllAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await api.put<{ data: { marked_read: number } }>(
        '/notifications/read-all',
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });
}
